import { useEffect, useState } from 'react';
import {
  Tree, Spin, App, Card, Tag, Tabs, Button, Modal, Form, Input, InputNumber,
  Popconfirm, ColorPicker, Select, Switch, Empty,
} from 'antd';
import type { DataNode } from 'antd/es/tree';
import { Users, User as UserIcon, Briefcase, Folder, Plus, Edit2, Trash2, FolderOpen } from 'lucide-react';
import {
  orgService,
  type OrgNode, type DepartmentGroup,
  type CreateDepartmentGroupRequest,
} from '@/services/rolesService';

/**
 * V1.0.32: Trang quản lý cây tổ chức + nhóm khoa phòng.
 *
 * 2 tab:
 *  1. Cây cơ cấu — hiển thị tree theo nhóm (nhóm → khoa) hoặc cây gốc
 *  2. Quản lý nhóm — CRUD department_groups + gán khoa vào nhóm
 */
export function OrgTreeTab() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [tree, setTree] = useState<OrgNode[]>([]);
  const [groups, setGroups] = useState<DepartmentGroup[]>([]);
  const [selected, setSelected] = useState<OrgNode | null>(null);

  // View mode trong tab cây cơ cấu
  const [viewMode, setViewMode] = useState<'tree' | 'by-group'>('tree');

  const reload = async () => {
    setLoading(true);
    try {
      const [t, g] = await Promise.all([
        orgService.getAdminTree(),
        orgService.listGroupsAdmin(),
      ]);
      setTree(t);
      setGroups(g);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  return (
    <Tabs
      defaultActiveKey="tree"
      items={[
        {
          key: 'tree',
          label: '🌳 Cây cơ cấu',
          children: (
            <TreeView
              tree={tree}
              groups={groups}
              viewMode={viewMode}
              setViewMode={setViewMode}
              selected={selected}
              setSelected={setSelected}
              onAssignGroup={async (deptId, groupId) => {
                try {
                  await orgService.assignDeptToGroup(deptId, groupId);
                  message.success('Đã cập nhật nhóm cho khoa');
                  reload();
                } catch (e: any) {
                  message.error(e.response?.data?.message ?? e.message);
                }
              }}
            />
          ),
        },
        {
          key: 'groups',
          label: '📁 Nhóm khoa phòng',
          children: (
            <GroupsManager
              groups={groups}
              onReload={reload}
            />
          ),
        },
      ]}
    />
  );
}

// ============================================================
// TAB 1: Cây cơ cấu + assign group
// ============================================================
function TreeView({
  tree, groups, viewMode, setViewMode, selected, setSelected, onAssignGroup,
}: {
  tree: OrgNode[];
  groups: DepartmentGroup[];
  viewMode: 'tree' | 'by-group';
  setViewMode: (m: 'tree' | 'by-group') => void;
  selected: OrgNode | null;
  setSelected: (n: OrgNode | null) => void;
  onAssignGroup: (deptId: string, groupId: string | null) => void;
}) {
  // Flatten tree → list khoa (giữ thông tin parentId để build cây)
  const flatten = (nodes: OrgNode[]): OrgNode[] => {
    const out: OrgNode[] = [];
    const walk = (ns: OrgNode[]) => {
      ns.forEach(n => { out.push(n); walk(n.children || []); });
    };
    walk(nodes);
    return out;
  };
  const flat = flatten(tree);

  // Tree data theo 2 mode
  const buildTreeView = (nodes: OrgNode[]): DataNode[] => nodes.map(n => ({
    key: n.id,
    title: <DeptNodeTitle node={n} />,
    children: buildTreeView(n.children || []),
  }));

  const buildByGroupView = (): DataNode[] => {
    // Group khoa theo groupId
    const byGroup = new Map<string, OrgNode[]>();
    const ungrouped: OrgNode[] = [];
    flat.forEach(n => {
      if (n.groupId) {
        if (!byGroup.has(n.groupId)) byGroup.set(n.groupId, []);
        byGroup.get(n.groupId)!.push(n);
      } else {
        ungrouped.push(n);
      }
    });

    const nodes: DataNode[] = [];
    // Theo thứ tự sortOrder của group
    groups.forEach(g => {
      const depts = byGroup.get(g.id) ?? [];
      nodes.push({
        key: `group-${g.id}`,
        title: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <FolderOpen size={14} style={{ color: g.color }} />
            <span style={{ fontWeight: 700, color: g.color }}>{g.name}</span>
            <Tag style={{ margin: 0, fontSize: 10 }}>{depts.length} khoa</Tag>
          </span>
        ),
        selectable: false,
        children: depts.map(d => ({
          key: d.id,
          title: <DeptNodeTitle node={d} />,
        })),
      });
    });
    if (ungrouped.length > 0) {
      nodes.push({
        key: 'group-none',
        title: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Folder size={14} style={{ color: '#bdc4cd' }} />
            <span style={{ fontWeight: 700, color: '#5b6875' }}>Chưa phân nhóm</span>
            <Tag style={{ margin: 0, fontSize: 10 }}>{ungrouped.length} khoa</Tag>
          </span>
        ),
        selectable: false,
        children: ungrouped.map(d => ({
          key: d.id,
          title: <DeptNodeTitle node={d} />,
        })),
      });
    }
    return nodes;
  };

  const treeData = viewMode === 'tree' ? buildTreeView(tree) : buildByGroupView();
  const allKeys = (function collect(): React.Key[] {
    if (viewMode === 'tree') {
      const out: React.Key[] = [];
      const walk = (ns: OrgNode[]) => ns.forEach(n => { out.push(n.id); walk(n.children || []); });
      walk(tree);
      return out;
    }
    return [...groups.map(g => `group-${g.id}`), 'group-none'];
  })();

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Select
          size="small"
          value={viewMode}
          onChange={setViewMode as any}
          options={[
            { value: 'tree', label: '🌳 Cây phân cấp (theo parent)' },
            { value: 'by-group', label: '📁 Gom theo nhóm khoa phòng' },
          ]}
          style={{ width: 320 }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <div style={{ fontSize: 11, color: '#7d8a98', marginBottom: 8 }}>
            Click vào khoa để xem chi tiết + đổi nhóm
          </div>
          <Tree
            showLine
            defaultExpandedKeys={allKeys}
            treeData={treeData}
            onSelect={(keys) => {
              const k = keys[0] as string | undefined;
              if (!k || k.startsWith('group-')) return;
              const node = flat.find(n => n.id === k);
              setSelected(node ?? null);
            }}
          />
        </div>

        <div>
          {selected ? (
            <Card title={`Chi tiết: ${selected.name}`} size="small">
              <div style={{ display: 'grid', gap: 12, fontSize: 13 }}>
                <Row label="Mã" value={<span className="mono">{selected.code}</span>} />
                <Row label="Tên đầy đủ" value={selected.name} />
                <Row label="Số nhân viên" value={<Tag color="blue">{selected.userCount}</Tag>} />
                <Row label="Đơn vị con" value={selected.children?.length ?? 0} />
                <Row label="Trưởng đơn vị" value={selected.managerName || <span style={{ color: '#adb6c0' }}>Chưa có</span>} />

                <div style={{ borderTop: '1px solid #f0f2f5', paddingTop: 12 }}>
                  <div style={{ width: 110, color: '#7d8a98', fontSize: 12, marginBottom: 6 }}>Nhóm khoa phòng:</div>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="-- Chưa phân nhóm --"
                    allowClear
                    value={selected.groupId ?? undefined}
                    onChange={(v) => onAssignGroup(selected.id, v ?? null)}
                    options={groups.filter(g => g.isActive).map(g => ({
                      value: g.id,
                      label: (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: g.color, display: 'inline-block' }} />
                          {g.name}
                        </span>
                      ),
                    }))}
                  />
                </div>
              </div>
            </Card>
          ) : (
            <Card size="small">
              <div style={{ color: '#7d8a98', textAlign: 'center', padding: 20 }}>
                Chọn 1 khoa từ cây bên trái để xem chi tiết + đổi nhóm
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function DeptNodeTitle({ node }: { node: OrgNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <Briefcase size={12} />
      <span style={{ fontWeight: 500 }}>{node.name}</span>
      <span className="mono" style={{ fontSize: 10, color: '#7d8a98' }}>· {node.code}</span>
      <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
        <Users size={10} style={{ verticalAlign: -2, marginRight: 4 }} />{node.userCount}
      </Tag>
      {node.groupName && (
        <Tag color={node.groupColor ?? 'default'} style={{ fontSize: 10, margin: 0 }}>
          {node.groupName}
        </Tag>
      )}
      {node.managerName && (
        <span style={{ fontSize: 11, color: '#7d8a98' }}>
          · <UserIcon size={10} style={{ verticalAlign: -1 }} /> {node.managerName}
        </span>
      )}
    </span>
  );
}

// ============================================================
// TAB 2: CRUD groups
// ============================================================
function GroupsManager({ groups, onReload }: {
  groups: DepartmentGroup[];
  onReload: () => void;
}) {
  const { message } = App.useApp();
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState<DepartmentGroup | null>(null);

  const handleDelete = async (g: DepartmentGroup) => {
    try {
      await orgService.deleteGroup(g.id);
      message.success(`Đã xóa nhóm "${g.name}". Các khoa thuộc nhóm này sẽ chuyển sang Chưa phân nhóm.`);
      onReload();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: '#5b6875' }}>
          Nhóm khoa phòng giúp gom các khoa lại theo tính chất công việc (vd: Lâm sàng, Cận lâm sàng, Hỗ trợ).
          <br />Áp dụng để gom hiển thị trong menu Tài liệu, Dự án...
        </div>
        <Button type="primary" icon={<Plus size={14} />} onClick={() => setOpenCreate(true)}>
          Thêm nhóm
        </Button>
      </div>

      {groups.length === 0 ? (
        <Empty description="Chưa có nhóm nào. Tạo nhóm đầu tiên để bắt đầu phân loại khoa phòng." />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {/* Header */}
          <div style={{
            display: 'grid', gridTemplateColumns: '40px 1fr 2fr 1fr 90px 80px 120px',
            gap: 8, padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#48535e',
            background: '#f5f7fa', borderRadius: 4,
          }}>
            <div></div>
            <div>Mã</div>
            <div>Tên nhóm</div>
            <div>Mô tả</div>
            <div style={{ textAlign: 'center' }}>Số khoa</div>
            <div style={{ textAlign: 'center' }}>Hoạt động</div>
            <div style={{ textAlign: 'right' }}>Thao tác</div>
          </div>
          {groups.map(g => (
            <div key={g.id} style={{
              display: 'grid', gridTemplateColumns: '40px 1fr 2fr 1fr 90px 80px 120px',
              gap: 8, padding: '10px 12px', fontSize: 13, alignItems: 'center',
              border: '1px solid #f0f2f5', borderRadius: 6, background: '#fff',
            }}>
              <div>
                <span style={{
                  width: 24, height: 24, borderRadius: '50%', background: g.color,
                  display: 'inline-block', verticalAlign: 'middle',
                }} />
              </div>
              <div className="mono" style={{ fontWeight: 600, color: '#1976D2' }}>{g.code}</div>
              <div style={{ fontWeight: 500 }}>{g.name}</div>
              <div style={{ fontSize: 12, color: '#5b6875' }}>{g.description || '—'}</div>
              <div style={{ textAlign: 'center' }}>
                <Tag color="blue">{g.departmentCount}</Tag>
              </div>
              <div style={{ textAlign: 'center' }}>
                {g.isActive
                  ? <Tag color="success">Bật</Tag>
                  : <Tag color="default">Tắt</Tag>}
              </div>
              <div style={{ textAlign: 'right', display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                <Button size="small" icon={<Edit2 size={12} />} onClick={() => setEditing(g)}>Sửa</Button>
                <Popconfirm
                  title="Xóa nhóm này?"
                  description={g.departmentCount > 0
                    ? `Có ${g.departmentCount} khoa đang thuộc nhóm này. Khi xóa, các khoa sẽ chuyển sang Chưa phân nhóm.`
                    : "Nhóm này chưa có khoa nào."}
                  onConfirm={() => handleDelete(g)}
                  okText="Xóa"
                  cancelText="Hủy"
                  okButtonProps={{ danger: true }}
                >
                  <Button size="small" danger icon={<Trash2 size={12} />}>Xóa</Button>
                </Popconfirm>
              </div>
            </div>
          ))}
        </div>
      )}

      {openCreate && (
        <GroupFormModal
          onClose={() => setOpenCreate(false)}
          onSaved={() => { setOpenCreate(false); onReload(); }}
        />
      )}
      {editing && (
        <GroupFormModal
          group={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onReload(); }}
        />
      )}
    </div>
  );
}

function GroupFormModal({ group, onClose, onSaved }: {
  group?: DepartmentGroup;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isEdit = !!group;

  useEffect(() => {
    if (group) {
      form.setFieldsValue({
        code: group.code,
        name: group.name,
        description: group.description ?? '',
        color: group.color,
        sortOrder: group.sortOrder,
        isActive: group.isActive,
      });
    } else {
      form.setFieldsValue({ color: '#1976D2', sortOrder: 100, isActive: true });
    }
  }, [group]);

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const color = typeof values.color === 'string'
        ? values.color
        : values.color?.toHexString?.() ?? '#1976D2';
      const payload: CreateDepartmentGroupRequest = {
        code: values.code,
        name: values.name,
        description: values.description || undefined,
        color,
        sortOrder: values.sortOrder,
      };
      if (isEdit && group) {
        await orgService.updateGroup(group.id, { ...payload, isActive: values.isActive });
        message.success('Đã cập nhật nhóm');
      } else {
        await orgService.createGroup(payload);
        message.success('Đã tạo nhóm');
      }
      onSaved();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal
      title={isEdit ? `Sửa nhóm: ${group?.name}` : 'Tạo nhóm khoa phòng mới'}
      open
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText={isEdit ? 'Lưu' : 'Tạo'}
      cancelText="Hủy"
      width={540}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="code" label="Mã nhóm"
          rules={[{ required: true, message: 'Bắt buộc' }, { pattern: /^[A-Z0-9_-]+$/, message: 'Chỉ chứa A-Z, 0-9, _, -' }]}
          extra="Vd: LAMSANG, CANLAMSANG, HOTRO">
          <Input placeholder="LAMSANG" disabled={isEdit} />
        </Form.Item>
        <Form.Item name="name" label="Tên nhóm" rules={[{ required: true, message: 'Bắt buộc' }]}>
          <Input placeholder="Khoa Lâm sàng" />
        </Form.Item>
        <Form.Item name="description" label="Mô tả">
          <Input.TextArea rows={2} placeholder="(tùy chọn)" />
        </Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="color" label="Màu nhãn">
            <ColorPicker showText />
          </Form.Item>
          <Form.Item name="sortOrder" label="Thứ tự sắp xếp" extra="Nhỏ → trên đầu">
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>
        </div>
        {isEdit && (
          <Form.Item name="isActive" label="Hoạt động" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{ width: 110, color: '#7d8a98', fontSize: 12 }}>{label}:</div>
      <div>{value}</div>
    </div>
  );
}
